// React
import React, { Component } from 'react';
import { connect } from 'react-redux';
// Routing
import { withRouter } from 'react-router-dom';
// Material
import { CircularProgress } from '@material-ui/core';
// Vendor
import { showNotify } from 'vendor/application/disptach';
// Infrastructure
import { IntegrationsAPI } from 'api/application/Integrations';
// Translations
import { withTranslation } from 'react-i18next';
// Styles
import 'views/Bots/Integrations/styles/index.scss';
// SVG
import { channelsSvg } from 'assets';

/*
 * Landing page for the Zendesk OAuth redirect
 * (/zendesk_full/integration/callback). Zendesk sends the browser back here
 * with ?code=<one-time-code>&state=<b64 json context>. The code is exchanged
 * and stored by the backend (PUT accounts/<account_id>/oauth); for an existing
 * channel the relink endpoint then flips it to auth_type='oauth'. Finally the
 * user is returned to the zendesk_full edit/create page with an ?oauth flag.
 */
class Redirect extends Component {
  state = { invalidContext: false };

  componentDidMount = () => {
    this.completeOauth();
  };

  getParamsURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return {
      code: urlParams.get('code'),
      state: urlParams.get('state'),
      error: urlParams.get('error'),
    };
  };

  decodeState = (state) => {
    try {
      return JSON.parse(window.atob(state)) || {};
    } catch (err) {
      return {};
    }
  };

  completeOauth = async () => {
    console.log('Zendesk OAuth redirect: completing oauth flow');

    const { access, user } = this.props.data;
    const { history } = this.props;
    const { code, state, error } = this.getParamsURL();

    const context = this.decodeState(state);
    // Both integrations share the registered redirect URI. Never infer the
    // originating integration from that URI or from the selected project.
    const isSunco = context.target === 'centrimanager_sunco';
    const isZendesk = context.target === 'zendesk_full' ||
      (!context.target && Boolean(context.project_id));
    if ((!isSunco && !isZendesk) ||
        (isSunco && !context.integration_id) ||
        (isZendesk && !context.project_id)) {
      this.setState({ invalidContext: true });
      return;
    }
    const { channel_id, subdomain, integration_id } = context;

    // Sunco must exchange the code using the CentriManager session that
    // initiated authorization, not the possibly unrelated Centribot session.
    if (isSunco) {
      const params = new URLSearchParams({ oauth_state: state });
      if (error || !code || !subdomain) {
        params.set('oauth', 'error');
      } else {
        params.set('oauth_code', code);
      }
      const manager = process.env.REACT_APP_URL_CENTRIMANAGER.replace(/\/$/, '');
      window.location.replace(`${manager}/integrations/sunco/edit/${encodeURIComponent(integration_id)}?${params.toString()}`);
      return;
    }

    const project_id = context.project_id;
    const account_id = context.account_id || user.account_id;
    const return_url = channel_id
      ? `/${project_id}/integrations/zendesk_full/edit/${channel_id}`
      : `/${project_id}/integrations/zendesk_full/create`;
    const redirectBack = (result) => history.push(`${return_url}?oauth=${result}`);

    if (error || !code || !subdomain) {
      redirectBack('error');
      return;
    }

    try {
      // exchange the code for tokens; the backend stores them account-level
      const obj = new IntegrationsAPI(account_id, access, this.props, {
        code: code,
        subdomain: subdomain,
      });
      await obj.complete_zendesk_full_oauth();

      // switch the existing channel over to oauth (webhooks are reused)
      if (channel_id) {
        const relink = new IntegrationsAPI(project_id, access, this.props);
        await relink.relink_zendesk_full_oauth(channel_id);
      }

      redirectBack('success');
    } catch (err) {
      this.props.dispatch(showNotify({ message: err, severity: 'error' }));
      redirectBack('error');
    }
  };

  render() {
    const { t } = this.props;
    const context = this.decodeState(this.getParamsURL().state);
    const channel = context.target === 'centrimanager_sunco' ? 'sunco' : 'zendesk_full';
    if (this.state.invalidContext) {
      return (
        <div className="cen_integrations">
          <div className="integration" role="alert">
            {t('integrations:channels.zendesk_full.oauth.invalid_context')}
          </div>
        </div>
      );
    }
    return (
      <div className={'cen_integrations'}>
        <div className={'header'}>
          <div className={'header_content'}>
            <div className={'header_image'}>{channelsSvg(channel)}</div>
            <div className={'header_title'}>
              <span className={'title'}>
                {t(`integrations:channels.${channel}.title`)}
              </span>
              <span className={'description'}>
                {t('integrations:channels.zendesk_full.oauth.title')}
              </span>
            </div>
          </div>
        </div>
        <div className={'content'}>
          <div className="loading">
            <CircularProgress color="primary" size={50} />
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    data: state,
  };
};

const redirect = connect(mapStateToProps)(Redirect);

export default withTranslation(['commons', 'common', 'integrations'])(
  withRouter(redirect),
);
